/**
 * Features Carousel
 * assets/features-carousel.js
 *
 * – Carrusel infinito (con clonado de nodos) o finito.
 * – Flechas de navegación siempre visibles.
 * – Autoplay configurable con pausa al interactuar.
 * – SEO-safe: los clones no tienen href ni son indexables.
 */

(function () {
  'use strict';

  /**
   * Neutraliza un clon para SEO y accesibilidad.
   */
  function seoSanitizeClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-nosnippet', '');
    clone.removeAttribute('id');

    clone.querySelectorAll('a').forEach(function (a) {
      a.removeAttribute('href');
      a.setAttribute('tabindex', '-1');
      a.setAttribute('role', 'presentation');
    });

    clone.querySelectorAll('img, video').forEach(function (el) {
      el.setAttribute('role', 'presentation');
      el.removeAttribute('alt');
    });
  }

  function initFeaturesCarousel(section) {
    var track = section.querySelector('.features-carousel__track');
    var prevBtn = section.querySelector('.features-carousel__arrow--prev');
    var nextBtn = section.querySelector('.features-carousel__arrow--next');

    if (!track) return;

    var originalCards = Array.from(track.querySelectorAll('.features-carousel__card'));
    if (originalCards.length === 0) return;

    var isInfinite = section.dataset.infinite === 'true' && originalCards.length > 1;
    var isAutoplay = section.dataset.autoplay === 'true';
    var autoplaySpeed = parseInt(section.dataset.autoplaySpeed, 10) || 3000;

    // ── Scroll distance: ancho de 1 card + gap ──
    function getScrollStep() {
      if (originalCards.length < 2) return track.clientWidth;
      var r1 = originalCards[0].getBoundingClientRect();
      var r2 = originalCards[1].getBoundingClientRect();
      return r2.left - r1.left;
    }

    // ── Infinite loop setup ──
    var setWidth = 0;

    if (isInfinite) {
      var makeCloneSet = function () {
        return originalCards.map(function (card) {
          var clone = card.cloneNode(true);
          clone.classList.add('features-carousel__card--clone');
          seoSanitizeClone(clone);
          return clone;
        });
      };

      // Prepend clones (reversed for visual order)
      makeCloneSet().reverse().forEach(function (clone) {
        track.prepend(clone);
      });

      // Append clones
      makeCloneSet().forEach(function (clone) {
        track.append(clone);
      });

      var computeSetWidth = function () {
        var all = track.querySelectorAll('.features-carousel__card');
        if (all.length < 2) return 0;
        var r1 = all[0].getBoundingClientRect();
        var r2 = all[1].getBoundingClientRect();
        return (r2.left - r1.left) * originalCards.length;
      };

      var initPosition = function () {
        setWidth = computeSetWidth();
        if (setWidth === 0) return;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = setWidth;
        requestAnimationFrame(function () {
          track.style.scrollBehavior = '';
        });
      };

      // Two frames to ensure layout is painted
      requestAnimationFrame(function () {
        requestAnimationFrame(initPosition);
      });

      // Teleport logic
      var teleporting = false;
      var BUFFER = 200;

      var teleportIfNeeded = function () {
        if (teleporting || setWidth === 0) return;
        var maxScroll = track.scrollWidth - track.clientWidth;

        if (track.scrollLeft < BUFFER) {
          teleporting = true;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft += setWidth;
          requestAnimationFrame(function () {
            track.style.scrollBehavior = '';
            teleporting = false;
          });
        } else if (track.scrollLeft > maxScroll - BUFFER) {
          teleporting = true;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft -= setWidth;
          requestAnimationFrame(function () {
            track.style.scrollBehavior = '';
            teleporting = false;
          });
        }
      };

      if ('onscrollend' in window) {
        track.addEventListener('scrollend', teleportIfNeeded, { passive: true });
      } else {
        var scrollEndTimer;
        track.addEventListener('scroll', function () {
          clearTimeout(scrollEndTimer);
          scrollEndTimer = setTimeout(teleportIfNeeded, 150);
        }, { passive: true });
      }

      // Recalculate on resize
      window.addEventListener('resize', function () {
        var prev = setWidth;
        setWidth = computeSetWidth();
        if (prev > 0 && setWidth !== prev && setWidth > 0) {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = (track.scrollLeft / prev) * setWidth;
          requestAnimationFrame(function () {
            track.style.scrollBehavior = '';
          });
        }
      }, { passive: true });
    }

    // ── Navigation buttons ──
    if (!prevBtn || !nextBtn) return;

    var updateButtons = function () {
      if (isInfinite) {
        prevBtn.classList.remove('is-disabled');
        nextBtn.classList.remove('is-disabled');
        return;
      }
      prevBtn.classList.toggle('is-disabled', track.scrollLeft <= 2);
      nextBtn.classList.toggle(
        'is-disabled',
        track.scrollLeft + track.clientWidth >= track.scrollWidth - 2
      );
    };

    setTimeout(updateButtons, 200);
    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons, { passive: true });

    prevBtn.addEventListener('click', function () {
      var step = getScrollStep();
      track.scrollBy({ left: -step, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', function () {
      var step = getScrollStep();
      track.scrollBy({ left: step, behavior: 'smooth' });
    });

    // ── Autoplay ──
    var autoplayTimer = null;

    var startAutoplay = function () {
      if (!isAutoplay) return;
      stopAutoplay();
      autoplayTimer = setInterval(function () {
        var step = getScrollStep();
        track.scrollBy({ left: step, behavior: 'smooth' });
      }, autoplaySpeed);
    };

    var stopAutoplay = function () {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    if (isAutoplay) {
      startAutoplay();

      track.addEventListener('touchstart', stopAutoplay, { passive: true });
      track.addEventListener('mousedown', stopAutoplay, { passive: true });
      track.addEventListener('touchend', function () {
        setTimeout(startAutoplay, 2000);
      }, { passive: true });
      track.addEventListener('mouseup', function () {
        setTimeout(startAutoplay, 2000);
      }, { passive: true });
    }
  }

  function init() {
    document.querySelectorAll('.features-carousel-section').forEach(initFeaturesCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
